import React, { useCallback, useState } from "react";
import { toast } from "sonner";
import { X, Loader2, CheckCircle2, CalendarPlus } from "lucide-react";
import TurnstileField from "./TurnstileField";
import SchoolSelector from "./SchoolSelector";
import { api } from "../lib/api";
import { getUTM } from "../lib/utm";
import { buildTrackingPayload } from "../lib/clickIds";
import { formatWebinarDate, formatTimeRange } from "../lib/webinarFormat";
import {
  trackWebinarRegistrationSubmit,
  trackWebinarRegistrationSuccess,
  trackWebinarRegistrationError,
} from "../lib/tracking";

// M900 — referral source is a constrained dropdown (instead of free text)
// so the admin team gets clean, groupable analytics. Picking the last
// option reveals an optional `Detaliază sursa` text input.
const REFERRAL_OPTIONS = [
  "Facebook / Instagram",
  "LinkedIn",
  "Email / newsletter",
  "Recomandare de la colegi",
  "Website FPED",
  "Logiscool",
  "Partener / inspectorat / instituție",
  "Eveniment / prezentare",
  "Google / căutare online",
  "Altă sursă",
];

const emptyForm = {
  first_name: "", last_name: "", email: "", phone: "",
  county: "", uat_name: "",
  school_name: "", school_code: "",
  city_original: "", manual_school_entry: false,
  position: "",
  referral_source: "",
  referral_source_detail: "",
  consent_processing: false,
  consent_marketing: false,
};

const inp =
  "w-full bg-white border border-[#E5E7EB] rounded-xl px-3 py-2.5 text-[#373A45] focus:ring-2 focus:ring-[#965EA7] focus:border-transparent outline-none";

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="text-xs font-semibold text-[#373A45] uppercase tracking-widest">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function SuccessPanel({ webinar, onClose }) {
  // "Add to calendar" (13.10.2026): the server builds both links, without the
  // meet link or the address. Absent on a webinar whose time it cannot read.
  const calendars = [
    ["Google Calendar", webinar.calendar?.google_url, "google"],
    ["Apple Calendar / Outlook", webinar.calendar?.ics_url, "ics"],
  ].filter(([, url]) => url);
  return (
    <div className="p-7 md:p-10 text-center" data-testid="webinar-registration-success">
      <div className="inline-flex h-14 w-14 rounded-full bg-[#EAF4EE] items-center justify-center text-[#1f6045] mb-4">
        <CheckCircle2 size={28} />
      </div>
      <h3 className="font-display text-2xl font-bold text-[#373A45]">
        Te-ai înscris cu succes!
      </h3>
      <p className="mt-3 text-[#4b4f5a] leading-relaxed">
        Mulțumim, te-am înregistrat la <strong>„{webinar.title}"</strong>.
      </p>
      <p className="mt-2 text-[#6B7280] text-sm">
        Ți-am trimis un email de confirmare cu data și ora. Linkul de acces îl
        vei primi într-un email separat, cu o zi înainte de eveniment. Verifică
        și folderul de Spam dacă nu găsești emailul în Inbox.
      </p>
      {calendars.length > 0 && (
        <div className="mt-5" data-testid="webinar-success-calendar">
          <p className="text-sm font-semibold text-[#373A45]">Adaugă webinarul în calendar</p>
          <div className="mt-2 flex flex-wrap justify-center gap-2">
            {calendars.map(([label, url, key]) => (
              <a
                key={key}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E7EB] bg-white px-4 py-2 text-sm font-semibold text-[#373A45] hover:border-[#965EA7] hover:text-[#7A4986]"
                data-testid={`webinar-success-calendar-${key}`}
              >
                <CalendarPlus size={15} /> {label}
              </a>
            ))}
          </div>
        </div>
      )}
      <button
        type="button"
        onClick={onClose}
        className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white font-semibold px-6 py-3"
        data-testid="webinar-success-close"
      >
        Închide
      </button>
    </div>
  );
}

// Reusable registration modal. Opens over whatever page invoked it (listing
// or dedicated detail page) — no routing change. The caller owns `successId`
// state so the success panel survives a re-render.
export default function WebinarRegistrationModal({ webinar, onClose, onRegistered, successId }) {
  const [form, setForm] = useState(emptyForm);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [loading, setLoading] = useState(false);

  const update = useCallback((k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  }, []);

  // Patch handler shape required by SchoolSelector — it sends ONLY the keys
  // that changed, parent merges them.
  const applySchoolPatch = useCallback((patch) => {
    setForm((f) => ({ ...f, ...patch }));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_processing) {
      toast.error("Te rugăm să accepți prelucrarea datelor pentru a finaliza înscrierea.");
      return;
    }
    if (!turnstileToken) {
      toast.error("Te rugăm să completezi verificarea anti-spam.");
      return;
    }
    setLoading(true);
    trackWebinarRegistrationSubmit(webinar);
    try {
      const utm = getUTM();
      // Analytics sidecar (event_id + click IDs + Meta/GA4 cookies) so the
      // server CAPI event can dedup against the browser Pixel via event_id.
      const tracking = buildTrackingPayload();
      const payload = { ...form, ...utm, ...tracking, turnstile_token: turnstileToken };
      if (payload.referral_source !== "Altă sursă") {
        payload.referral_source_detail = "";
      }
      const r = await api.post(`/webinars/${webinar.id}/register`, payload);
      const registrationId = r.data?.registration_id;
      // Pixel eventID = client event_id so it dedups with the server CAPI event.
      trackWebinarRegistrationSuccess(webinar, tracking.event_id);
      onRegistered(registrationId);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Nu am putut salva înscrierea. Te rugăm să încerci din nou.";
      const code = err?.response?.status || "network";
      trackWebinarRegistrationError(webinar, code);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="webinar-registration-modal"
    >
      <div
        className="bg-white rounded-3xl border border-[#E5E7EB] shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
          <div>
            <div className="text-xs font-semibold text-[#965EA7] uppercase tracking-widest">Înscriere webinar</div>
            <h3 className="font-display text-lg font-bold text-[#373A45] mt-0.5 leading-tight">
              {webinar.title}
            </h3>
            <div className="text-xs text-[#6B7280] mt-1">
              {formatWebinarDate(webinar.date)} · {formatTimeRange(webinar.start_time, webinar.duration_minutes)}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#6B7280] hover:text-[#373A45]"
            aria-label="Închide"
            data-testid="webinar-modal-close"
          >
            <X size={20} />
          </button>
        </div>

        {successId ? (
          <SuccessPanel webinar={webinar} onClose={onClose} />
        ) : (
          <form onSubmit={submit} className="p-5 md:p-6 space-y-4" data-testid="webinar-registration-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Prenume *" testid="webinar-form-firstname">
                <input required value={form.first_name} onChange={update("first_name")} className={`${inp} clarity-mask`} data-clarity-mask="true" />
              </Field>
              <Field label="Nume *" testid="webinar-form-lastname">
                <input required value={form.last_name} onChange={update("last_name")} className={`${inp} clarity-mask`} data-clarity-mask="true" />
              </Field>
              <Field label="Email *" testid="webinar-form-email">
                <input required type="email" value={form.email} onChange={update("email")} className={`${inp} clarity-mask`} data-clarity-mask="true" />
              </Field>
              <Field label="Telefon *" testid="webinar-form-phone">
                <input required value={form.phone} onChange={update("phone")} className={`${inp} clarity-mask`} data-clarity-mask="true" />
              </Field>
            </div>

            {/* M900 — județ → localitate (UAT) → școală. Same component the
                public LeadForm uses, so the school directory + manual fallback
                behave identically here. */}
            <div data-testid="webinar-form-school-selector">
              <SchoolSelector
                value={{
                  county: form.county,
                  uat_name: form.uat_name,
                  school_name: form.school_name,
                  school_code: form.school_code,
                  city_original: form.city_original,
                  manual_school_entry: form.manual_school_entry,
                }}
                onChange={applySchoolPatch}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Funcție / Rol" testid="webinar-form-position">
                <input
                  value={form.position}
                  onChange={update("position")}
                  placeholder="ex. profesor, director"
                  className={inp}
                />
              </Field>
              <Field label="Cum ai aflat de webinar?" testid="webinar-form-referral">
                <select
                  value={form.referral_source}
                  onChange={update("referral_source")}
                  className={inp}
                  data-testid="webinar-form-referral-select"
                >
                  <option value="">Selectează o opțiune...</option>
                  {REFERRAL_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </Field>
            </div>
            {form.referral_source === "Altă sursă" && (
              <Field label="Detaliază sursa (opțional)" testid="webinar-form-referral-detail">
                <input
                  value={form.referral_source_detail}
                  onChange={update("referral_source_detail")}
                  placeholder="ex. articol pe ..., podcast, prietena mea..."
                  className={inp}
                />
              </Field>
            )}

            <div className="space-y-2 pt-2">
              <label className="flex items-start gap-2 text-sm text-[#373A45]" data-testid="webinar-consent-processing">
                <input
                  type="checkbox"
                  checked={form.consent_processing}
                  onChange={update("consent_processing")}
                  className="mt-0.5"
                  required
                />
                <span>
                  Sunt de acord cu prelucrarea datelor personale pentru a finaliza
                  înscrierea la acest webinar. <span className="text-[#E63946]">*</span>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm text-[#6B7280]" data-testid="webinar-consent-marketing">
                <input
                  type="checkbox"
                  checked={form.consent_marketing}
                  onChange={update("consent_marketing")}
                  className="mt-0.5"
                />
                <span>
                  (Opțional) Doresc să primesc informări despre cursurile și webinariile
                  viitoare organizate de Fundația pentru Educație Digitală.
                </span>
              </label>
            </div>

            <TurnstileField
              onVerify={setTurnstileToken}
              onExpire={() => setTurnstileToken(null)}
              onError={() => setTurnstileToken(null)}
            />

            <button
              type="submit"
              disabled={loading}
              data-testid="webinar-form-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white font-semibold px-6 py-3.5 disabled:opacity-60 transition-all"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : null}
              Înscrie-mă la webinar
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
